import React, { useState, useEffect } from 'react';
import { Box, Typography, TableContainer, Table, TableBody, TableCell, TableRow, Card, CardHeader, CardContent } from '@mui/material';
import { LocalShipping, Devices, Schedule } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import buildUrl from '../../buildUrl';
import useApi from '../../hooks/useApi';
import RichTooltip from '../../RichTooltip';
import TypedValue from '../../TypedValue';
import { useTheme } from '@mui/material/styles';
import { lighten } from '@mui/system';
import DevicesChart from '../charts/DevicesChart';
import EmailOpensHeatmap from '../charts/EmailOpensHeatmap';
import ReportsCardHeader from '../cards/ReportsCardHeader';
import Opens from '../cards/Opens';
import Unsubscribes from '../cards/Unsubscribes';
import Clicks from '../cards/Clicks';
import Shares from '../cards/Shares';
import BestPerformingGroup from '../cards/BestPerformingGroup';
import SentimentCard from '../cards/SentimentCard';
import ReadTimeCategoryCard from '../cards/ReadTimeCategoryCard';
import ReadTimeByDeviceCard from '../cards/ReadTimeByDeviceCard';
import { yellow, purple } from '@mui/material/colors';

const ClickableTableRow = ({ onChangeFilter, colour, children }) => {
    return (
        <TableRow
            hover
            onClick={onChangeFilter}
            sx={{
                backgroundColor: lighten(colour, 0.8),
                '&.MuiTableRow-root:hover': {
                    backgroundColor: lighten(colour, 0.7)
                }
            }}
        >
            {children}
        </TableRow>
    );
};

const Difference = ({ a, b }) => {
    const theme = useTheme();
    const change = b - a;

    if (change === 0) {
        return '=';
    }

    return (
        <Box component="span" sx={{ color: theme.palette.text.secondary }}>{change}</Box>
    );
};

const Distribution = ({ selected, sent, delivered, failed, bounced, onChangeFilter }) => {
    const theme = useTheme();

    return (
        <Card
            sx={{
                height: '100%',
                m: 0,
                p: 0,
                '& .MuiCardHeader-action': {
                    alignSelf: 'center'
                }
            }}>
            <CardHeader
                title="Distribution"
                sx={{ m: 0, py: 1 }}
                slotProps={{
                    title: {
                        variant: 'h6',
                        fontWeight: 'normal'
                    }
                }}
                avatar={<LocalShipping />}
                action={(
                    <RichTooltip>
                        <>
                            <Typography variant="body1" fontWeight={500} sx={{ pb: 0.5 }}>If your Selected figure isn't what you expected</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary} sx={{ pb: 0.5 }}>The figure shown here may not seem to match the total number of Contacts selected as your Audience during the Sending process</Typography>
                            <Typography variant="body2" fontWeight={500} sx={{ pb: 0.5 }}>Why?</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>- A Contact may appear in more than one Group but will only be selected to be sent to <strong>once</strong> in an email campaign.</Typography>
                            <Typography variant="body2" color={theme.palette.text.secondary}>- Any 'Suppressed' Contacts will <strong>not</strong> be selected to be sent to.</Typography>
                        </>
                    </RichTooltip>
                )}
            />
            <CardContent
                sx={{
                    m: 0,
                    p: 0,
                    '&:last-child': {
                        paddingBottom: 0
                    },
                    '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                        borderBottom: 0
                    }
                }}
            >
                <TableContainer>
                    <Table size="small">
                        <TableBody>
                            <TableRow
                                sx={{
                                    backgroundColor: lighten(theme.palette.success.light, 0.9)
                                }}
                            >
                                <TableCell variant="head">
                                    Selected
                                </TableCell>
                                <TableCell align="right" width="80%">
                                    <TypedValue value={selected} type="number" accuracy={0} />
                                </TableCell>
                                <TableCell />
                            </TableRow>
                            <TableRow
                                sx={{
                                    backgroundColor: lighten(theme.palette.success.light, 0.8)
                                }}
                            >
                                <TableCell variant="head">
                                    Sent
                                </TableCell>
                                <TableCell align="right">
                                    <TypedValue value={sent} type="number" accuracy={0} />
                                </TableCell>
                                <TableCell align="right">
                                    <Difference
                                        a={selected}
                                        b={sent}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow
                                sx={{
                                    backgroundColor: lighten(theme.palette.success.light, 0.7)
                                }}
                            >
                                <TableCell variant="head">
                                    Delivered
                                </TableCell>
                                <TableCell align="right">
                                    <TypedValue value={delivered} type="number" accuracy={0} />
                                </TableCell>
                                <TableCell align="right">
                                    <Difference
                                        a={selected}
                                        b={delivered}
                                    />
                                </TableCell>
                            </TableRow>
                            <ClickableTableRow
                                onChangeFilter={() => onChangeFilter('bounced')}
                                colour={theme.palette.warning.light}
                            >
                                <TableCell variant="head">
                                    Bounced
                                </TableCell>
                                <TableCell align="right">
                                    <TypedValue value={bounced} type="number" accuracy={0} />
                                </TableCell>
                                <TableCell />
                            </ClickableTableRow>
                            <ClickableTableRow
                                onChangeFilter={() => onChangeFilter('failed')}
                                colour={theme.palette.error.light}
                            >
                                <TableCell variant="head">
                                    Failed
                                </TableCell>
                                <TableCell align="right">
                                    <TypedValue value={failed} type="number" accuracy={0} />
                                </TableCell>
                                <TableCell />
                            </ClickableTableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};

const NewSummary = ({ email, onChangeTab, onChangeSubscriberStatusFilter = null }) => {
    const theme = useTheme();
    const [devices, setDevices] = useState([]);
    const [heatmapData, setHeatmapData] = useState(null);
    const [bestGroup, setBestGroup] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { handleGet } = useApi();

    const handleFetchDevices = async () => {
        const url = buildUrl(`reports/${email.id}/devices?filterByOpens=false`);
        const response = await handleGet(url);

        if (!response.ok) {
            return;
        }

        setDevices(await response.json());
    };

    const handleFetchHeatmapData = async () => {
        const response = await handleGet(`reports/${email.id}/heatmap`);

        if (!response.ok) {
            return;
        }

        setHeatmapData(await response.json());
    };

    const handleFetchBestGroup = async () => {
        const response = await handleGet(`reports/${email.id}/best-group`);

        if (!response.ok) {
            return;
        }

        setBestGroup(await response.json());
    };

    useEffect(() => {
        handleFetchDevices();
        handleFetchHeatmapData();
        handleFetchBestGroup();
    }, []);

    useEffect(() => {
        (devices && heatmapData) && setIsLoading(false);
    }, [devices, heatmapData]);

    if (isLoading) {
        return (
            <LoadingOverlay />
        );
    }

    return (
        <Box sx={{ margin: 2 }}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Distribution
                        selected={email.selected}
                        sent={email.sent}
                        failed={email.failed}
                        bounced={email.bounced}
                        delivered={email.sent - email.bounced}
                        onChangeFilter={onChangeSubscriberStatusFilter}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Unsubscribes
                        total={email.unsubscribes}
                        system={email.systemUnsubscribes}
                        selected={email.selected}
                        feedback={email.feedback}
                        onChangeSubscriberStatusFilter={onChangeSubscriberStatusFilter}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Opens
                        onClick={() => onChangeSubscriberStatusFilter('opened')}
                        unique={email.uniqueOpens}
                        total={email.totalOpens}
                        teamsCampaigns={email.teamsCampaigns}
                        qrCodes={email.qrCodes}
                        channelTotals={email.channelTotals}
                        selected={email.selected}
                        allChannelTotal={email.allChannelOpens}
                        socialActions={email.socialActions}
                        onChangeTab={onChangeTab}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Clicks
                        onClick={() => onChangeSubscriberStatusFilter('clicked')}
                        unique={email.uniqueClicks}
                        total={email.totalClicks}
                        teamsCampaigns={email.teamsCampaigns}
                        qrCodes={email.qrCodes}
                        channelTotals={email.channelTotals}
                        selected={email.selected}
                        allChannelTotal={email.allChannelClicks}
                        socialActions={email.socialActions}
                        onChangeTab={onChangeTab}
                        noLinks={Boolean(email.numberOfLinks === 0)}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <ReadTimeCategoryCard
                        a={email}
                        aColour={yellow[600]}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <ReadTimeByDeviceCard
                        a={email}
                        aColour={purple[600]}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card sx={{ height: '100%' }}>
                        <CardHeader
                            sx={{
                                m: 0,
                                py: 1,
                                '& .MuiCardHeader-action': {
                                    alignSelf: 'center'
                                }
                            }}
                            slotProps={{
                                title: {
                                    variant: 'h6',
                                    fontWeight: 'normal'
                                }
                            }}
                            avatar={<Devices />}
                            title="Devices"
                        />
                        <CardContent>
                            {devices && devices.length > 0 ? (
                                <DevicesChart
                                    label="Devices"
                                    data={devices.map(e => ({ value: e.total, name: e.label }))}
                                />
                            ) : (
                                <Typography variant="body2">No activity</Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <SentimentCard
                        a={email}
                        aColour={theme.palette.primary.main}
                        bColour={theme.palette.primary.main}
                    />
                </Grid>
                <Grid size={12}>
                    <Card sx={{ height: '100%' }}>
                        <ReportsCardHeader
                            title="Peak Engagement Heatmap"
                            subheader="Unique Opens data is used to show peak engagement"
                            icon={<Schedule />}
                        />
                        <CardContent>
                            <EmailOpensHeatmap data={heatmapData} />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <BestPerformingGroup bestGroup={bestGroup} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Shares
                        selected={email.selected}
                        socialActions={email.socialActions}
                        onChangeTab={onChangeTab}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default NewSummary;